import { Suspense, lazy, useEffect, useMemo } from "react";
import { isExpired } from "react-jwt";
import { Outlet, useLocation } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { Box } from "@mui/material";
import { apiGetDomains } from "../api/systemLibrary";
import { useGlobalData } from "../hooks/useGlobalData";
import { getURLDomain, logoutFunction } from "../common/utils";
import useDomainStore from "../store/domain";
import { useConfigStore } from "../store/config";
import RouteFallback from "../component/RouteFallback";
import "../assets/scss/page/index.scss";
import { useUrfReceiveEvents } from "../hooks/urfEvents";
import { useQuery } from "@tanstack/react-query";
import { apiGetFeatureFlagListAll } from "../api/pm";
import { useFeatureFlagStore } from "../store/featureFlagStore";
import { getMuiLocaleBundles } from "../utils/muiLocales";
import { getCurrentTenantId } from "../utils/tenant";
import { getUserSettings } from "../api/uam";
import { DEFAULT_LANGUAGE, VALID_LANGUAGES } from "../constants";
import { toDayjsFormat } from "@freyr/freyr-react-common";

const FreyrLibraryProvider = lazy(() =>
  import("@freyr/freyr-react-common").then((m) => ({ default: m.FreyrLibraryProvider }))
);

const Layouts = () => {
  const { token, tenant, domain: cookieDomain } = useGlobalData();
  const setFlags = useFeatureFlagStore((s) => s.setFlags);
  const { language, updateLanguage, updateRegionalSettings, timeZone, dateFormat, timeFormat } = useConfigStore();
  const URLDomain = getURLDomain();
  const redirectURL = `https://portal.${URLDomain}`;
  const { updateDomain, domainValue } = useDomainStore();
  const location = useLocation();
  const isBpmnRoute = location.pathname.startsWith("/bpmn");
  const isAuthenticated = Boolean(token && !isExpired(token));

  const themeWithLocale = useMemo(() => {
    const { core, grid, pickers } = getMuiLocaleBundles(language);
    return createTheme({ palette: {} }, core, grid, pickers);
  }, [language]);

  useUrfReceiveEvents();

  useEffect(() => {
    if (!isAuthenticated) {
      logoutFunction();
      window.location.href = `${redirectURL}?redirect=${window.location.origin}`;
    }
  }, [isAuthenticated, redirectURL]);

  useEffect(() => {
    if (!cookieDomain) {
      updateDomain({
        domainName: "ALL",
        domainId: "-1",
      });
      return;
    }
    apiGetDomains()
      .then((res) => {
        const result = res?.data;
        if (Array.isArray(result) && result.length > 0) {
          const curDomain = result.find((o) => o.domainPhrase === cookieDomain);
          if (curDomain) {
            updateDomain({
              domainName: curDomain?.domainPhrase,
              domainId: curDomain?.value,
            });
          }
        }
      })
      .catch(() => {});
  }, [cookieDomain, updateDomain]);

  const { data: featureFlagList } = useQuery(
    ["fetchFeatureFlagListAll"],
    () => apiGetFeatureFlagListAll(),
    {
      enabled: isAuthenticated,
      retry: false,
      refetchOnWindowFocus: false,
      select: (response) => response?.data || {},
    }
  );

  useEffect(() => {
    if (featureFlagList) {
      setFlags(featureFlagList);
    }
  }, [featureFlagList, setFlags]);

  const { data: userSettings } = useQuery(
    ["fetchUserSettings"],
    () => getUserSettings(),
    {
      enabled: isAuthenticated,
      retry: false,
      refetchOnWindowFocus: false,
      select: (response) => response?.data,
    }
  );

  useEffect(() => {
    if (!userSettings) {
      return;
    }
    updateRegionalSettings({
      timeZone: userSettings.timeZone,
      dateFormat: toDayjsFormat(userSettings.dateFormat),
      timeFormat: toDayjsFormat(userSettings.timeFormat),
    });
    if (VALID_LANGUAGES.includes(userSettings.language)) {
      updateLanguage(userSettings.language);
    } else {
      updateLanguage(DEFAULT_LANGUAGE);
    }
  }, [userSettings, updateRegionalSettings, updateLanguage]);

  if (!isAuthenticated) {
    return <RouteFallback />;
  }

  return (
    <ThemeProvider theme={themeWithLocale}>
      <Suspense fallback={<RouteFallback />}>
        <FreyrLibraryProvider
          token={token}
          language={language}
          tenant={
            isBpmnRoute
              ? getCurrentTenantId()
              : tenant
          }
          domain={cookieDomain ? domainValue.domainId : undefined}
          domainName={cookieDomain ? domainValue.domainName : undefined}
          regionalSettings={{
            timeZone,
            dateFormat,
            timeFormat,
          }}
        >
          <Box className="custom-page" data-testid="custom-page">
            <Suspense fallback={<RouteFallback />}>
              <Outlet />
            </Suspense>
          </Box>
        </FreyrLibraryProvider>
      </Suspense>
    </ThemeProvider>
  );
};

export default Layouts;
